/**
 * Result of grid cell index resolution.
 * Null values indicate out-of-bounds or unparsable grid template.
 */
export interface GridCellIndexResult {
    /**
     * Zero-based cell index on X-axis (column), or null if out of bounds or unparsable.
     */
    cellIndexX: number | null;

    /**
     * Zero-based cell index on Y-axis (row), or null if out of bounds or unparsable.
     */
    cellIndexY: number | null;
}

/**
 * Resolves the grid cell index from client coordinates (absolute viewport coordinates).
 * Converts pixel position to zero-based cell indices by parsing CSS Grid template.
 * Supports both regular grids and CSS subgrid elements (reads parent's track sizes).
 *
 * @param areaElement - The grid container element (must have display: grid or subgrid)
 * @param clientX - Absolute X coordinate in viewport (e.g., from MouseEvent.clientX)
 * @param clientY - Absolute Y coordinate in viewport (e.g., from MouseEvent.clientY)
 * @returns Object with cellIndexX and cellIndexY (0-based), or null for out-of-bounds/unparsable
 *
 * @example
 * ```typescript
 * const result = resolveGridCellIndex(areaElement, event.clientX, event.clientY);
 * if (result.cellIndexX !== null && result.cellIndexY !== null) {
 *   console.log(`Clicked cell: column ${result.cellIndexX}, row ${result.cellIndexY}`);
 * }
 * ```
 */
export function resolveGridCellIndex(
    areaElement: HTMLElement,
    clientX: number,
    clientY: number
): GridCellIndexResult {
    const computedStyle = window.getComputedStyle(areaElement);
    const rawColumns = computedStyle.gridTemplateColumns;
    const rawRows = computedStyle.gridTemplateRows;

    // Resolve track sizes (handles both regular grid and subgrid)
    const columnWidths = resolveTrackSizes(rawColumns, areaElement, 'columns');
    if (columnWidths === null) {
        return { cellIndexX: null, cellIndexY: null };
    }

    const rowHeights = resolveTrackSizes(rawRows, areaElement, 'rows');
    if (rowHeights === null) {
        return { cellIndexX: null, cellIndexY: null };
    }

    // Get element position in viewport
    const rect = areaElement.getBoundingClientRect();

    // Calculate relative position within the grid
    const relativeX = clientX - rect.left;
    const relativeY = clientY - rect.top;

    // Find cell index for each axis
    const cellIndexX = findCellIndex(columnWidths, relativeX);
    const cellIndexY = findCellIndex(rowHeights, relativeY);

    return { cellIndexX, cellIndexY };
}

/**
 * Resolves track sizes for a grid axis. If the element uses subgrid,
 * reads the parent grid's resolved track sizes and extracts the relevant slice.
 *
 * @param rawTemplate - The computed gridTemplateColumns/Rows value
 * @param element - The grid element
 * @param axis - Which axis to resolve ('columns' or 'rows')
 * @returns Array of pixel sizes for the tracks this element spans, or null if unparsable
 */
export function resolveTrackSizes(
    rawTemplate: string,
    element: HTMLElement,
    axis: 'columns' | 'rows'
): number[] | null {
    // Regular grid: parse directly
    if (!isSubgrid(rawTemplate)) {
        return parseGridTemplate(rawTemplate);
    }

    // Subgrid: read parent's track sizes and extract the relevant slice
    const parent = element.parentElement;
    if (!parent) {
        return null;
    }

    const parentStyle = window.getComputedStyle(parent);
    const parentTemplate = axis === 'columns'
        ? parentStyle.gridTemplateColumns
        : parentStyle.gridTemplateRows;

    const parentTracks = parseGridTemplate(parentTemplate);
    if (parentTracks === null) {
        return null;
    }

    // Determine which parent tracks this element spans
    const elementStyle = window.getComputedStyle(element);

    const startRaw = axis === 'columns' ? elementStyle.gridColumnStart : elementStyle.gridRowStart;
    const endRaw = axis === 'columns' ? elementStyle.gridColumnEnd : elementStyle.gridRowEnd;

    const startIndex = parseInt(startRaw) - 1; // to 0-based

    let endIndex: number;
    if (endRaw.startsWith('span')) {
        const span = parseInt(endRaw.replace('span', '').trim());
        endIndex = startIndex + span;
    } else {
        endIndex = parseInt(endRaw) - 1;
    }

    return parentTracks.slice(startIndex, endIndex);
}

/**
 * Checks if a computed grid template value indicates subgrid.
 * Chrome returns "subgrid [line-names] ..." for subgrid elements.
 */
function isSubgrid(template: string): boolean {
    return template.startsWith('subgrid');
}

/**
 * Parses CSS grid template (e.g., "40px 40px 40px 40px") into array of pixel values.
 * Supports repeat() syntax: "repeat(10, 40px)" expands to [40, 40, 40, ...].
 * Returns null if template contains unsupported units (fr, auto, etc.) or cannot be parsed.
 *
 * @param template - The grid template string (e.g., "40px 40px 40px" or "repeat(10, 40px)")
 * @returns Array of pixel values, or null if unparsable
 */
function parseGridTemplate(template: string): number[] | null {
    if (!template || template === 'none') {
        return null;
    }

    // Handle repeat() syntax: repeat(10, 40px) → [40, 40, 40, ...]
    const repeatMatch = template.match(/^repeat\((\d+),\s*([^)]+)\)$/);
    if (repeatMatch && repeatMatch[1] && repeatMatch[2]) {
        const count = parseInt(repeatMatch[1], 10);
        const sizeStr = repeatMatch[2].trim();

        // Only support px units in repeat
        if (!sizeStr.endsWith('px')) {
            return null;
        }

        const size = parseFloat(sizeStr);
        if (isNaN(size) || isNaN(count)) {
            return null;
        }

        return Array(count).fill(size);
    }

    // Regular template parsing (space-separated values)
    const parts = template.trim().split(/\s+/);
    const values: number[] = [];

    for (const part of parts) {
        // Only support px units
        if (!part.endsWith('px')) {
            // Skip subgrid line names like [] brackets
            continue;
        }

        const value = parseFloat(part);
        if (isNaN(value)) {
            return null;
        }

        values.push(value);
    }

    return values.length > 0 ? values : null;
}

/**
 * Finds the cell index for a given relative position within cumulative sizes.
 * Returns null if position is out of bounds (negative or beyond last cell).
 *
 * @param sizes - Array of cell sizes in pixels
 * @param relativePosition - Relative position in pixels (within the grid container)
 * @returns Zero-based cell index, or null if out of bounds
 */
function findCellIndex(sizes: number[], relativePosition: number): number | null {
    if (relativePosition < 0) {
        return null;
    }

    let cumulative = 0;
    for (let i = 0; i < sizes.length; i++) {
        const size = sizes[i];
        if (size === undefined) {
            continue;
        }
        cumulative += size;
        if (relativePosition < cumulative) {
            return i;
        }
    }

    // Position is beyond the last cell
    return null;
}
